import React, { useCallback, useState } from 'react';

import Axios from 'axios';
import { FORM_ERROR } from 'final-form';
import { Field, Form } from 'react-final-form';
import { useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { resetPassword } from 'api/auth';
import Button from 'components/shared/Button';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import FormErrorText from 'components/shared/FormErrorText';
import AuthTitle from 'components/shared/Typography/AuthTitle';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import useValidationSchema from 'hooks/useValidationSchema';

import { GP_AUTH_SIGNIN_ROUTE } from '../../Routes/constants';
import { useGuestPortalRouteBuilder } from '../../Routes/functions';
import { validationSchema } from './validationSchema';

function useResetPassword() {
  const { title, guestPortalId } = useParams();
  const [isSuccess, setIsSuccess] = useState(false);
  const isCustomDomain = location.pathname.includes('/gp/');
  const rootPath = isCustomDomain ? `${location.origin}/gp` : location.origin;
  const redirectUri = [rootPath, title, guestPortalId].join('/');

  const handleSubmit = useCallback(
    (payload) => {
      return resetPassword({ ...payload, guestPortalId, redirectUri })
        .then(() => setIsSuccess(true))
        .catch((err) => {
          if (Axios.isAxiosError(err) && err.response.status === 401) {
            return { email: err.response.data?.error || GENERAL_ERROR_MESSAGE };
          }

          return {
            [FORM_ERROR]: 'No account is associated with this email address',
          };
        });
    },
    [guestPortalId, redirectUri],
  );

  return { isSuccess, handleSubmit };
}

function ForgotPassword() {
  const validate = useValidationSchema(validationSchema);
  const { isSuccess, handleSubmit } = useResetPassword();
  const { buildRoute } = useGuestPortalRouteBuilder();

  return (
    <>
      {!isSuccess && (
        <Form onSubmit={handleSubmit} validate={validate}>
          {({ handleSubmit, submitting, hasValidationErrors, submitError }) => {
            return (
              <form onSubmit={handleSubmit}>
                <AuthTitle>Reset Password</AuthTitle>

                <Field noMargin component={FormGroupAdapter} name="email" placeholder="Email" type="text" />
                {<FormErrorText>{submitError}</FormErrorText>}

                <FormActions isSpaceBetween isRight>
                  <Link to={buildRoute(GP_AUTH_SIGNIN_ROUTE)} className="link">
                    Go back
                  </Link>

                  <Button htmlType="submit" disabled={submitting || hasValidationErrors}>
                    Send
                  </Button>
                </FormActions>
              </form>
            );
          }}
        </Form>
      )}

      {isSuccess && (
        <div>
          <AuthTitle>
            Thanks! We’ve sent a recovery
            <br />
            link to your email.
          </AuthTitle>

          <FormActions isCenter>
            <Link to={buildRoute(GP_AUTH_SIGNIN_ROUTE)}>
              <Button htmlType="button">Login</Button>
            </Link>
          </FormActions>
        </div>
      )}
    </>
  );
}

export default ForgotPassword;
